import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { IconCulinaryExperience, IconExperience, IconOutdoorExperience } from '../icon';
import BevSafeText from '../bev-safe-text';
import type { CategoryIcon } from './types';

const ICONS = {
	experience: IconExperience,
	culinary: IconCulinaryExperience,
	outdoor: IconOutdoorExperience,
} satisfies Record<CategoryIcon, unknown>;

type Props = {
	icon: CategoryIcon;
	label: string;
	// 'tag' (default) is the pale pill shown on product cards; 'plain' is the
	// bare icon+label row used on the feature card, per Figma.
	variant?: 'tag' | 'plain';
	className?: string;
};

export default function CategoryChip({
	icon,
	label,
	variant = 'tag',
	className,
}: Props): ReactElement {
	const Icon = ICONS[icon] ?? IconExperience;
	// The typography class (text-caption/text-label) is kept out of cn()'s merge:
	// tailwind-merge doesn't know these custom classes aren't text-color utilities,
	// so combining them with a text-text-* color class through cn() makes it treat
	// them as conflicting and silently drop one - see category-chip.cy.tsx.
	const typographyClass = variant === 'tag' ? 'text-caption' : 'text-label';
	return (
		<span
			className={`${typographyClass} ${cn(
				'inline-flex w-fit items-center',
				// Figma's "tag" instance itemSpacing is a flat 10px at every tier
				// (not 6 mobile / 10 desktop), and its own padding matches the
				// tag-padding-x/-y tokens exactly (8/10 horizontal, 4/6 vertical) -
				// the "plain" variant's icon-to-label gap is a separate, genuinely
				// flat 6px ("demographic type" instance), unrelated to the tag's.
				variant === 'tag'
					? 'gap-100 rounded-sm bg-warm-stone-20 px-tag-padding-x py-tag-padding-y text-text-primary'
					: 'gap-060 text-text-secondary',
				className
			)}`}
		>
			<span className="inline-flex shrink-0">
				<Icon width={14} height={14} />
			</span>
			<BevSafeText>{label}</BevSafeText>
		</span>
	);
}
